import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, Plus, Clock, CheckCircle, XCircle, Activity, Eye, Search, Filter, Calendar, Building2, Upload, Play, Pause, Square } from 'lucide-react';
import { whatsappService } from 'api/services/whatsapp.service';
import { organizationsService } from 'api/services/organizations.service';
import { WhatsAppCampaign, PaginatedResponse, CampaignInsights } from 'types';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
import Pagination from 'components/Common/Pagination';
import { ITEMS_PER_PAGE, FEATURE_FLAGS } from 'utils/constants';
import CampaignCreateModal from 'components/WhatsApp/CampaignCreateModal';
import { useAuth } from 'hooks/useAuth';
import OrganizationFilter from 'components/Filters/OrganizationFilter';

const WhatsAppCampaigns: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const currentRole = (currentOrganization?.role || user?.role)?.toLowerCase();

  const [data, setData] = useState<PaginatedResponse<WhatsAppCampaign> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCampaign, setSelectedCampaign] = useState<WhatsAppCampaign | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();
  const [recipientRows, setRecipientRows] = useState<any[] | null>(null);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientStatus, setRecipientStatus] = useState('ALL');
  const [recipientPage, setRecipientPage] = useState(0);
  const [recipientTotalPages, setRecipientTotalPages] = useState(0);
  const [resending, setResending] = useState(false);
  const [campaignInsights, setCampaignInsights] = useState<CampaignInsights | null>(null);

  // Lazy-load button-click insights whenever the detail modal opens for a campaign.
  useEffect(() => {
    let active = true;
    setCampaignInsights(null);
    const id = selectedCampaign?.id;
    if (!id) return;
    (async () => {
      const res = await whatsappService.getCampaignInsights(id);
      if (active && res.success && res.data) setCampaignInsights(res.data);
    })();
    return () => { active = false; };
  }, [selectedCampaign?.id]);

  const loadRecipients = async (campaignId: string, status = recipientStatus, page = 0) => {
    setRecipientsLoading(true);
    const res = await whatsappService.getCampaignRecipients(campaignId, { status, page, size: 50 });
    if (res.success && res.data) {
      setRecipientRows(res.data.content || []);
      setRecipientPage(res.data.page ?? page);
      setRecipientTotalPages(res.data.totalPages ?? 0);
    } else {
      setRecipientRows([]);
      setRecipientTotalPages(0);
    }
    setRecipientsLoading(false);
  };

  const exportRecipients = async (campaignId: string) => {
    const res = await whatsappService.getCampaignRecipients(campaignId, { status: recipientStatus, page: 0, size: 100000 });
    const rows: any[] = res.success && res.data ? (res.data.content || []) : [];
    const csv = ['phone,status,reason', ...rows.map((r) => `${r.phone || ''},${r.status || ''},"${(r.errorReason || '').replace(/"/g, '""')}"`)].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `campaign-${campaignId}-recipients.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const resendFailed = async (campaignId: string) => {
    setResending(true);
    const res = await whatsappService.resendFailed(campaignId);
    setResending(false);
    if (res.success) {
      setSelectedCampaign(null);
      setRecipientRows(null);
      window.location.reload();
    } else {
      alert(res.error || 'Resend failed');
    }
  };

  // Campaign lifecycle: Start (DRAFT/SCHEDULED), Pause (RUNNING), Resume (PAUSED ->
  // start), Stop/Cancel (RUNNING/PAUSED). Resume reuses startCampaign — the backend
  // accepts a PAUSED -> RUNNING transition.
  const [lifecycleBusyId, setLifecycleBusyId] = useState<string | null>(null);
  const runLifecycle = async (
    campaign: WhatsAppCampaign,
    action: 'start' | 'pause' | 'cancel',
    label: string,
  ) => {
    if (!campaign.id) return;
    if (action === 'cancel' && !window.confirm(`Stop campaign "${campaign.name}"? This cannot be undone.`)) return;
    setLifecycleBusyId(campaign.id);
    const call =
      action === 'start' ? whatsappService.startCampaign
        : action === 'pause' ? whatsappService.pauseCampaign
          : whatsappService.cancelCampaign;
    const res = await call(campaign.id);
    setLifecycleBusyId(null);
    if (res.success) {
      fetchCampaigns();
      // Keep the open detail modal in sync with the new status.
      if (res.data && selectedCampaign?.id === campaign.id) setSelectedCampaign(res.data);
    } else {
      alert(res.error || `Failed to ${label} campaign`);
    }
  };

  // Lifecycle buttons for a campaign (shared by the list row and the detail modal).
  const lifecycleButtons = (campaign: WhatsAppCampaign) => (
    <>
      {(campaign.status === 'DRAFT' || campaign.status === 'SCHEDULED') && (
        <button
          onClick={() => runLifecycle(campaign, 'start', 'start')}
          disabled={lifecycleBusyId === campaign.id}
          className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition disabled:opacity-50"
          title="Start"
        >
          <Play className="w-4 h-4" />
        </button>
      )}
      {campaign.status === 'RUNNING' && (
        <button
          onClick={() => runLifecycle(campaign, 'pause', 'pause')}
          disabled={lifecycleBusyId === campaign.id}
          className="p-2 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded-lg transition disabled:opacity-50"
          title="Pause"
        >
          <Pause className="w-4 h-4" />
        </button>
      )}
      {campaign.status === 'PAUSED' && (
        <button
          onClick={() => runLifecycle(campaign, 'start', 'resume')}
          disabled={lifecycleBusyId === campaign.id}
          className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition disabled:opacity-50"
          title="Resume"
        >
          <Play className="w-4 h-4" />
        </button>
      )}
      {(campaign.status === 'RUNNING' || campaign.status === 'PAUSED') && (
        <button
          onClick={() => runLifecycle(campaign, 'cancel', 'stop')}
          disabled={lifecycleBusyId === campaign.id}
          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition disabled:opacity-50"
          title="Stop"
        >
          <Square className="w-4 h-4" />
        </button>
      )}
    </>
  );

  // Organization selector
  const [selectedOrgId, setSelectedOrgId] = useState<string | 'all'>(
    FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA
      ? (currentOrganization?.id || '')
      : 'all'
  );

  // Determine organizationId to pass to API
  const getOrganizationId = () => {
    if (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') {
      if (selectedOrgId === 'all' || !selectedOrgId) {
        return undefined;
      }
      return selectedOrgId;
    }
    if (currentOrganization?.id && currentOrganization.id !== 'synthetic-org') {
      return currentOrganization.id;
    }
    return undefined;
  };

  // Check if organization selection is required but not selected
  const needsOrgSelection = FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA &&
                            (currentRole === 'master_admin' || currentRole === 'super_admin') &&
                            (!selectedOrgId || selectedOrgId === 'all');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    if (!needsOrgSelection) {
      fetchCampaigns();
    }
  }, [currentPage, searchQuery, statusFilter, dateFrom, dateTo, selectedOrgId, needsOrgSelection]);

  const fetchCampaigns = async () => {
    if (needsOrgSelection) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const orgId = getOrganizationId();
    const result = await whatsappService.getCampaigns({
      page: currentPage,
      size: ITEMS_PER_PAGE,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      organizationId: orgId,
    });

    if (result.success && result.data) {
      // Apply client-side filters
      let filteredData = result.data.data;

      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        filteredData = filteredData.filter(c =>
          c.name.toLowerCase().includes(searchLower) ||
          (c.templateName && c.templateName.toLowerCase().includes(searchLower))
        );
      }

      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        filteredData = filteredData.filter(c => {
          const campaignDate = c.createdAt ? new Date(c.createdAt) : null;
          return campaignDate && campaignDate >= fromDate;
        });
      }

      if (dateTo) {
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999); // End of day
        filteredData = filteredData.filter(c => {
          const campaignDate = c.createdAt ? new Date(c.createdAt) : null;
          return campaignDate && campaignDate <= toDate;
        });
      }

      setData({
        ...result.data,
        data: filteredData,
        total: filteredData.length,
      });
    } else {
      setError(result.error || 'Failed to fetch campaigns');
    }

    setLoading(false);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
  };

  const getStatusBadge = (status: string) => {
    switch (status.toUpperCase()) {
      case 'COMPLETED':
        return (
          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400 rounded text-xs font-medium flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Completed
          </span>
        );
      case 'RUNNING':
        return (
          <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-400 rounded text-xs font-medium flex items-center gap-1">
            <Activity className="w-3 h-3 animate-spin" />
            Running
          </span>
        );
      case 'SCHEDULED':
        return (
          <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-400 rounded text-xs font-medium flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Scheduled
          </span>
        );
      case 'DRAFT':
        return (
          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded text-xs font-medium">
            Draft
          </span>
        );
      // Open and waiting — not finished, not mid-send, and NOT paused. Given its own
      // colour because reading it as Paused sends somebody looking for a stall that
      // is not there.
      case 'STANDING':
        return (
          <span className="px-2 py-1 bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-300 rounded text-xs font-medium">
            Open
          </span>
        );
      case 'PAUSED':
        return (
          <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-400 rounded text-xs font-medium">
            Paused
          </span>
        );
      case 'CANCELLED':
        return (
          <span className="px-2 py-1 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-400 rounded text-xs font-medium flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Cancelled
          </span>
        );
      default:
        return <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded text-xs">{status}</span>;
    }
  };

  const calculateSuccessRate = (campaign: WhatsAppCampaign) => {
    if (!campaign.totalRecipients || campaign.totalRecipients === 0) return 0;
    return Math.round((((campaign.messagesDelivered ?? campaign.deliveredCount) || 0) / campaign.totalRecipients) * 100);
  };

  if (loading && !data) return <LoadingSpinner />;
  if (error && !needsOrgSelection && !data) return <ErrorMessage message={error} onRetry={fetchCampaigns} />;
  if (!data && !needsOrgSelection) return null;

  return (
    <div className="space-y-4">
      {/* Organization Filter - For master_admin, super_admin, and service_admin */}
      {(currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <OrganizationFilter
            value={selectedOrgId === 'all' ? '' : selectedOrgId}
            onChange={(value) => {
              if (FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA) {
                setSelectedOrgId(value || selectedOrgId);
              } else {
                setSelectedOrgId(value || 'all');
              }
            }}
            className="w-full md:w-96"
            requireSelection={FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA}
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Show friendly message when organization selection is required */}
      {needsOrgSelection && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <Building2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">Select an Organization</h3>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                Please select an organization from the dropdown above to view WhatsApp campaigns.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Hidden when org selection required */}
      {!needsOrgSelection && data && (
        <>
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                <Megaphone className="w-7 h-7" />
                WhatsApp Campaigns
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Total: {data?.total || 0} campaigns
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                onClick={() => navigate('/whatsapp/campaigns/new')}
              >
                <Upload className="w-5 h-5" />
                New CSV Campaign
              </button>
              <button
                className="tl-btn tl-btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus className="w-5 h-5" />
                Create Campaign
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search campaigns by title or template..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Filter Options */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); handleFilterChange(); }}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All</option>
              <option value="DRAFT">Draft</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="RUNNING">Running</option>
              <option value="PAUSED">Paused</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          {/* Date Range Filter */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <label className="text-sm text-gray-600 dark:text-gray-400">From:</label>
            <input
              type="datetime-local"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); handleFilterChange(); }}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">To:</label>
            <input
              type="datetime-local"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); handleFilterChange(); }}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Clear Filters */}
          {(searchQuery || statusFilter !== 'all' || dateFrom || dateTo) && (
            <button
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setDateFrom('');
                setDateTo('');
                handleFilterChange();
              }}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Active Filters Summary */}
        {(statusFilter !== 'all' || dateFrom || dateTo) && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600 dark:text-gray-400">Active filters:</span>
            {statusFilter !== 'all' && (
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-400 rounded text-xs">
                Status: {statusFilter}
              </span>
            )}
            {dateFrom && (
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-400 rounded text-xs">
                From: {new Date(dateFrom).toLocaleDateString()}
              </span>
            )}
            {dateTo && (
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-400 rounded text-xs">
                To: {new Date(dateTo).toLocaleDateString()}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Campaigns List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Recipients
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Schedule
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {data.data.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Megaphone className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{campaign.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Created: {campaign.createdAt ? new Date(campaign.createdAt).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900 dark:text-white">{campaign.templateName || 'N/A'}</span>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(campaign.status)}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {campaign.totalRecipients || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                        <span>Sent: {(campaign.messagesSent ?? campaign.sentCount) || 0}</span>
                        <span>Delivered: {(campaign.messagesDelivered ?? campaign.deliveredCount) || 0}</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          className="bg-green-600 h-2 rounded-full transition-all"
                          style={{
                            width: `${calculateSuccessRate(campaign)}%`,
                          }}
                        />
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-green-600 dark:text-green-400">{calculateSuccessRate(campaign)}% success</span>
                        {(campaign.failedCount || 0) > 0 && (
                          <span className="text-red-600 dark:text-red-400">{campaign.failedCount} failed</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {campaign.scheduledAt ? (
                      <div className="text-sm">
                        <p className="text-gray-900 dark:text-white">
                          {new Date(campaign.scheduledAt).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(campaign.scheduledAt).toLocaleTimeString()}
                        </p>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">Not scheduled</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      {lifecycleButtons(campaign)}
                      <button
                        onClick={() => campaign.id && navigate(`/whatsapp/campaigns/${campaign.id}`)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data.data.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
            <Pagination
              currentPage={currentPage}
              totalPages={data.totalPages}
              onPageChange={setCurrentPage}
              totalItems={data.total}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </div>
        )}
      </div>
        </>
      )}

      {/* Campaign Details Modal */}
      {selectedCampaign && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => { setSelectedCampaign(null); setRecipientRows(null); }}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedCampaign.name}</h2>
                {selectedCampaign.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{selectedCampaign.description}</p>
                )}
                <div className="mt-2 flex items-center gap-2">
                  {getStatusBadge(selectedCampaign.status)}
                  <div className="flex items-center gap-1">{lifecycleButtons(selectedCampaign)}</div>
                </div>
              </div>
              <button
                onClick={() => { setSelectedCampaign(null); setRecipientRows(null); }}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              {/* Campaign Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Template</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedCampaign.templateName || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Audience Type</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedCampaign.audienceType || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Recipients</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedCampaign.totalRecipients || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Messages Per Minute</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedCampaign.messagesPerMinute || 'Default'}</p>
                </div>
              </div>

              {/* Statistics */}
              <div className="border-t dark:border-gray-700 pt-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Campaign Statistics</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                    <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Sent</p>
                    <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{(selectedCampaign.messagesSent ?? selectedCampaign.sentCount) || 0}</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3">
                    <p className="text-xs text-green-600 dark:text-green-400 font-medium">Delivered</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">{(selectedCampaign.messagesDelivered ?? selectedCampaign.deliveredCount) || 0}</p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3">
                    <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Read</p>
                    <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{(selectedCampaign.messagesRead ?? selectedCampaign.readCount) || 0}</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-3">
                    <p className="text-xs text-red-600 dark:text-red-400 font-medium">Failed</p>
                    <p className="text-2xl font-bold text-red-900 dark:text-red-100">{(selectedCampaign.messagesFailed ?? selectedCampaign.failedCount) || 0}</p>
                  </div>
                </div>
              </div>

              {/* Insights — computed from messages, with failure reasons */}
              {selectedCampaign.insights && (
                <div className="border-t dark:border-gray-700 pt-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Insights</h3>
                  <div className="flex flex-wrap gap-4 mb-3 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Delivery rate: </span>
                      <span className="font-semibold text-green-600 dark:text-green-400">{selectedCampaign.insights.deliveryRatePct ?? 0}%</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Read rate: </span>
                      <span className="font-semibold text-purple-600 dark:text-purple-400">{selectedCampaign.insights.readRatePct ?? 0}%</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Pending: </span>
                      <span className="font-semibold text-gray-700 dark:text-gray-200">{selectedCampaign.insights.pending ?? 0}</span>
                    </div>
                  </div>
                  {(selectedCampaign.insights.failureBreakdown?.length ?? 0) > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Failure reasons</p>
                      <div className="space-y-1">
                        {selectedCampaign.insights.failureBreakdown!.map((f) => (
                          <div key={f.code} className="flex items-center justify-between text-sm bg-red-50 dark:bg-red-900/20 rounded px-3 py-1.5">
                            <span className="text-gray-700 dark:text-gray-300">
                              <span className="font-mono text-xs text-red-600 dark:text-red-400 mr-2">{f.code}</span>
                              {f.reason}
                            </span>
                            <span className="font-semibold text-red-700 dark:text-red-300">{f.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Button clicks — quick-reply/URL button engagement */}
              {campaignInsights && campaignInsights.quickReplyClicks.length > 0 && (
                <div className="border-t dark:border-gray-700 pt-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Button clicks</h3>
                  <div className="space-y-1">
                    {campaignInsights.quickReplyClicks.map((b, i) => (
                      <div key={i} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-700/50 rounded px-3 py-1.5">
                        <span className="text-gray-700 dark:text-gray-300">{b.label}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-semibold text-gray-900 dark:text-white">{b.clicks}</span> clicks · {b.clickRate}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Per-recipient delivery breakdown */}
              <div className="border-t dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Recipients</h3>
                  <div className="flex items-center gap-2">
                    {recipientRows && (
                      <>
                        <select
                          value={recipientStatus}
                          onChange={(e) => { setRecipientStatus(e.target.value); if (selectedCampaign.id) loadRecipients(selectedCampaign.id, e.target.value, 0); }}
                          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded"
                        >
                          <option value="ALL">All statuses</option>
                          <option value="DELIVERED">Delivered</option>
                          <option value="READ">Read</option>
                          <option value="SENT">Sent</option>
                          <option value="FAILED">Failed</option>
                        </select>
                        <button onClick={() => selectedCampaign.id && exportRecipients(selectedCampaign.id)}
                          className="text-xs px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200">
                          Export CSV
                        </button>
                        <button onClick={() => selectedCampaign.id && resendFailed(selectedCampaign.id)} disabled={resending}
                          className="text-xs px-3 py-1 bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50">
                          {resending ? 'Resending…' : 'Resend to failed'}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => selectedCampaign.id && loadRecipients(selectedCampaign.id, recipientStatus, 0)}
                      className="text-xs px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200"
                    >
                      {recipientsLoading ? 'Loading…' : recipientRows ? 'Refresh' : 'View per-recipient breakdown'}
                    </button>
                  </div>
                </div>
                {recipientRows && (
                  recipientRows.length === 0 ? (
                    <p className="text-sm text-gray-400">No per-recipient messages found.</p>
                  ) : (
                    <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                          <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                            <th className="px-3 py-2">Phone</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Reason</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {recipientRows.map((r, i) => (
                            <tr key={i}>
                              <td className="px-3 py-1.5 font-mono text-xs text-gray-700 dark:text-gray-300">{r.phone}</td>
                              <td className="px-3 py-1.5">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  r.status === 'READ' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                                  : r.status === 'DELIVERED' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                  : r.status === 'FAILED' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                                  {r.status}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400">{r.errorReason || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                )}
                {recipientRows && recipientTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
                    <button disabled={recipientPage <= 0}
                      onClick={() => selectedCampaign.id && loadRecipients(selectedCampaign.id, recipientStatus, recipientPage - 1)}
                      className="px-2 py-1 rounded disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">Prev</button>
                    <span>Page {recipientPage + 1} of {recipientTotalPages}</span>
                    <button disabled={recipientPage >= recipientTotalPages - 1}
                      onClick={() => selectedCampaign.id && loadRecipients(selectedCampaign.id, recipientStatus, recipientPage + 1)}
                      className="px-2 py-1 rounded disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">Next</button>
                  </div>
                )}
              </div>

              {/* Timeline */}
              <div className="border-t dark:border-gray-700 pt-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Timeline</h3>
                <div className="space-y-2">
                  {selectedCampaign.createdAt && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Created:</span>
                      <span className="font-medium dark:text-white">{new Date(selectedCampaign.createdAt).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedCampaign.scheduledAt && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Scheduled:</span>
                      <span className="font-medium dark:text-white">{new Date(selectedCampaign.scheduledAt).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedCampaign.startedAt && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Started:</span>
                      <span className="font-medium dark:text-white">{new Date(selectedCampaign.startedAt).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedCampaign.pausedAt && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Paused:</span>
                      <span className="font-medium dark:text-white">{new Date(selectedCampaign.pausedAt).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedCampaign.completedAt && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Completed:</span>
                      <span className="font-medium dark:text-white">{new Date(selectedCampaign.completedAt).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t dark:border-gray-700">
              <button
                onClick={() => { setSelectedCampaign(null); setRecipientRows(null); }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Create Modal */}
      <CampaignCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => fetchCampaigns()}
      />
    </div>
  );
};

export default WhatsAppCampaigns;
